import { FC } from "react";
import { Button, Card, Checkbox, DatePicker, Form, Input, InputNumber, Select, Space } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import moment from "moment";

import {
    AnalysisOutputType,
    DealStructuredOutput,
    FlexibleAnalysisOutput,
    ProjectType,
    RfxStructuredOutput,
    StructuredOutput,
    StructuredOutputType,
} from "../../../../types/ProjectTypes";
import { VendorInput } from "../../../vendor/VendorInput";
import { UserSelect } from "../../../../common/components/UserSelect";
import { LeversTab } from "./LeversTab";
import { RfxVendorPicker } from "./RfxVendorPicker";

interface FlexibleStructuredOutputsTabProps {
    output: FlexibleAnalysisOutput;
    onChange: (output: FlexibleAnalysisOutput) => void;
    projectType: ProjectType;
}

function createEmptyDealStructuredOutput(): DealStructuredOutput {
    return {
        type: StructuredOutputType.DEAL,
        label: "Deal",
        data: {
            dealInfo: {
                vendorKey: "",
                value: 0,
                autoRenewal: false,
                orderScore: 0,
                optimizedScore: 0,
            },
        },
    };
}

function createEmptyRfxStructuredOutput(): RfxStructuredOutput {
    return {
        type: StructuredOutputType.RFX,
        data: { vendors: [] },
    };
}

// ── RFx structured output form ──────────────────────────────────────

interface RfxStructuredOutputFormProps {
    structuredOutput: RfxStructuredOutput;
    onChange: (updated: RfxStructuredOutput) => void;
}

const RfxStructuredOutputForm: FC<RfxStructuredOutputFormProps> = ({
    structuredOutput,
    onChange,
}) => {
    const { data } = structuredOutput;
    const keys = data.vendors.map((v) => v.vendorKey);

    return (
        <Form labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} layout="horizontal" size="small">
            <Form.Item label="Candidate vendors" required>
                <RfxVendorPicker
                    value={keys}
                    onChange={(nextKeys) =>
                        onChange({
                            ...structuredOutput,
                            data: { vendors: nextKeys.map((vendorKey) => ({ vendorKey })) },
                        })
                    }
                />
            </Form.Item>
        </Form>
    );
};

// ── Dollar amount input (value stored in USD cents) ──────────────────

interface DollarInputProps {
    valueCents: number | undefined;
    onChange: (cents: number | undefined) => void;
}

const DollarInput: FC<DollarInputProps> = ({ valueCents, onChange }) => (
    <InputNumber
        style={{ width: "100%" }}
        min={0}
        value={valueCents != null ? valueCents / 100 : undefined}
        onChange={(v) => onChange(typeof v === "number" ? Math.round(v * 100) : undefined)}
        formatter={(v) => (v == null ? "" : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","))}
        parser={(v) => (v ? Number(v.replace(/,/g, "")) : ("" as unknown as number))}
    />
);

// ── Deal structured output form ──────────────────────────────────────

interface DealStructuredOutputFormProps {
    structuredOutput: DealStructuredOutput;
    onChange: (updated: DealStructuredOutput) => void;
}

const DealStructuredOutputForm: FC<DealStructuredOutputFormProps> = ({
    structuredOutput,
    onChange,
}) => {
    const { data } = structuredOutput;
    const dealInfo = data.dealInfo ?? {
        vendorKey: "",
        value: 0,
        autoRenewal: false,
        orderScore: 0,
        optimizedScore: 0,
    };

    const updateData = (patch: Partial<typeof data>) => {
        onChange({ ...structuredOutput, data: { ...data, ...patch } });
    };

    const updateDealInfo = (patch: Partial<typeof dealInfo>) => {
        updateData({ dealInfo: { ...dealInfo, ...patch } });
    };

    return (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <Form labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} layout="horizontal" size="small">
                <Form.Item label="Label">
                    <Input
                        value={structuredOutput.label}
                        onChange={(e) => onChange({ ...structuredOutput, label: e.target.value })}
                    />
                </Form.Item>
                <Form.Item label="Vendor" required>
                    <VendorInput
                        key={dealInfo.vendorKey || "empty"}
                        value={dealInfo.vendorKey}
                        onSelect={(vendorKey) => updateDealInfo({ vendorKey })}
                    />
                </Form.Item>
                <Form.Item label="Contract value ($)" required>
                    <DollarInput
                        valueCents={dealInfo.value || undefined}
                        onChange={(cents) => updateDealInfo({ value: cents ?? 0 })}
                    />
                </Form.Item>
                <Form.Item label="Start date">
                    <DatePicker
                        style={{ width: "100%" }}
                        value={
                            dealInfo.orderStartDate ? moment(dealInfo.orderStartDate) : undefined
                        }
                        onChange={(d) => updateDealInfo({ orderStartDate: d?.toISOString() })}
                    />
                </Form.Item>
                <Form.Item label="End date">
                    <DatePicker
                        style={{ width: "100%" }}
                        value={dealInfo.orderEndDate ? moment(dealInfo.orderEndDate) : undefined}
                        onChange={(d) => updateDealInfo({ orderEndDate: d?.toISOString() })}
                    />
                </Form.Item>
                <Form.Item label="Owner">
                    <UserSelect
                        userId={dealInfo.ownerId}
                        onSelect={(userId) => updateDealInfo({ ownerId: userId })}
                    />
                </Form.Item>
                <Form.Item label="Increase ($)">
                    <DollarInput
                        valueCents={dealInfo.increase}
                        onChange={(cents) => updateDealInfo({ increase: cents })}
                    />
                </Form.Item>
                <Form.Item label="Auto renewal">
                    <Checkbox
                        checked={dealInfo.autoRenewal}
                        onChange={(e) => updateDealInfo({ autoRenewal: e.target.checked })}
                    />
                </Form.Item>
                <Form.Item label="Est. savings low ($)">
                    <DollarInput
                        valueCents={dealInfo.estimatedSavingsLow}
                        onChange={(cents) => updateDealInfo({ estimatedSavingsLow: cents })}
                    />
                </Form.Item>
                <Form.Item label="Est. savings high ($)">
                    <DollarInput
                        valueCents={dealInfo.estimatedSavingsHigh}
                        onChange={(cents) => updateDealInfo({ estimatedSavingsHigh: cents })}
                    />
                </Form.Item>
                <Form.Item label="Realized savings ($)">
                    <DollarInput
                        valueCents={dealInfo.realizedSavings}
                        onChange={(cents) => updateDealInfo({ realizedSavings: cents })}
                    />
                </Form.Item>
            </Form>

            <Card size="small" title="Scoring & Levers" style={{ marginTop: 12 }}>
                <LeversTab
                    output={{ ...data, type: AnalysisOutputType.DEAL }}
                    onChange={updateData}
                />
            </Card>
        </Space>
    );
};

// ── Main component ───────────────────────────────────────────────────

export const FlexibleStructuredOutputsTab: FC<FlexibleStructuredOutputsTabProps> = ({
    output,
    onChange,
    projectType,
}) => {
    const structuredOutputs = output.structuredOutputs || [];

    const hasDealInOutput = structuredOutputs.some((so) => so.type === StructuredOutputType.DEAL);
    const hasRfxInOutput = structuredOutputs.some((so) => so.type === StructuredOutputType.RFX);
    const dealDisabled = hasDealInOutput;
    const dealDisabledReason = hasDealInOutput
        ? "Only one deal structured output is allowed per analysis"
        : undefined;
    const rfxDisabled = projectType !== ProjectType.RFX || hasRfxInOutput;
    const rfxDisabledReason =
        projectType !== ProjectType.RFX
            ? "RFx structured output is only valid on RFx projects"
            : hasRfxInOutput
              ? "Only one RFx structured output is allowed per analysis"
              : undefined;

    const updateAt = (index: number, updated: StructuredOutput) => {
        const next = [...structuredOutputs];
        next[index] = updated;
        onChange({ ...output, structuredOutputs: next });
    };

    const removeAt = (index: number) => {
        const next = structuredOutputs.filter((_, i) => i !== index);
        onChange({ ...output, structuredOutputs: next.length > 0 ? next : undefined });
    };

    const add = (type: StructuredOutputType) => {
        switch (type) {
            case StructuredOutputType.DEAL:
                onChange({
                    ...output,
                    structuredOutputs: [...structuredOutputs, createEmptyDealStructuredOutput()],
                });
                break;
            case StructuredOutputType.RFX:
                onChange({
                    ...output,
                    structuredOutputs: [...structuredOutputs, createEmptyRfxStructuredOutput()],
                });
                break;
        }
    };

    return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            {structuredOutputs.map((so, index) => (
                <Card
                    key={index}
                    size="small"
                    title={`${
                        so.type === StructuredOutputType.DEAL ? so.label || so.type : so.type
                    } (#${index + 1})`}
                    extra={
                        <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={() => removeAt(index)}
                        />
                    }
                >
                    {so.type === StructuredOutputType.DEAL && (
                        <DealStructuredOutputForm
                            structuredOutput={so}
                            onChange={(updated) => updateAt(index, updated)}
                        />
                    )}
                    {so.type === StructuredOutputType.RFX && (
                        <RfxStructuredOutputForm
                            structuredOutput={so}
                            onChange={(updated) => updateAt(index, updated)}
                        />
                    )}
                </Card>
            ))}

            <Select<StructuredOutputType>
                placeholder="Add structured output..."
                style={{ width: 250 }}
                value={undefined}
                onChange={(type) => add(type)}
                options={[
                    {
                        label: dealDisabledReason ? `Deal — ${dealDisabledReason}` : "Deal",
                        value: StructuredOutputType.DEAL,
                        disabled: dealDisabled,
                    },
                    {
                        label: rfxDisabledReason ? `RFx — ${rfxDisabledReason}` : "RFx",
                        value: StructuredOutputType.RFX,
                        disabled: rfxDisabled,
                    },
                ]}
            />
        </Space>
    );
};
